<template>
  <div class="seeker-job-card">
    <section class="relative">
      <section>
        <alert-banner
          v-if="isPending"
          data-testid="job-card-banner-pending"
          :status="helperStatus.Warn"
          class="w-full"
          :auto-scroll-to-banner="false"
        >
          <span>{{ pendingReviewMessage }}</span>
        </alert-banner>
        <alert-banner
          v-if="showApplicantsBanner"
          data-testid="job-card-banner-applicants"
          :status="helperStatus.Neutral"
          class="w-full"
          :auto-scroll-to-banner="false"
        >
          <span>{{ newApplicantsTitle }}</span>
        </alert-banner>
        <div class="flex flex-row w-full gap-4 relative">
          <a class="block w-full" :href="resolvedHref" @click="emitJobClick">
            <div class="text-primary w-full">
              <h3 class="job-text text-h5 mt-8" :class="{ '!text-grey-text-subdued': !isActiveOrPending }">
                {{ job.title }}
              </h3>
              <p
                class="job-text text-small text-grey-body"
                :class="{ 'text-grey-text-subdued': !isActiveOrPending }"
                data-testid="applicants-text"
              >
                {{ totalApplicantsText }}
              </p>
              <p v-if="!fcOwnsJob" class="job-text mb-8 text-body-2 text-grey-text-subdued">{{ jobDate }}</p>
              <p v-else-if="fcOwnsJob" class="job-text mb-8 text-body-2 text-grey-text-subdued">Posted by Family Concierge</p>
            </div>
          </a>
          <Button
            v-if="displayDots"
            id="modal-toggle"
            class="seeker-job-card-modal absolute top-8 right-8"
            :class="popupClasses"
            variant="text"
            :aria-label="modalToggleLabel"
            @click="emitManageJob"
          >
            <MoreIcon />
          </Button>
        </div>
      </section>
    </section>
    <slot></slot>
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import pluralize from 'pluralize';
import type { RouteLocationRaw } from 'vue-router';
import Button from '@/components/button/Button.vue';
import AlertBanner from '@/components/alert_banner/AlertBanner.vue';
import MoreIcon from '@/graphics/icons/MoreIcon.vue';
import JobPresenter from '@/lib/presenters/job_presenter';
import helperStatus from '@/helpers/status';
import { Account } from '@/models/account';

export default defineComponent({
  name: 'SeekerJobCard',
  components: {
    AlertBanner,
    Button,
    MoreIcon,
  },
  props: {
    job: {
      type: Object as () => any,
      required: true,
    },
    account: {
      type: Object as () => Account,
      required: true,
    },
    /** When true, click is handled by parent and router navigation is prevented */
    preventNavigationOnClick: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['click', 'manage-job'],
  data(): {
    helperStatus: typeof helperStatus;
  } {
    return {
      helperStatus,
    };
  },
  computed: {
    // UI State
    modalToggleLabel(): string {
      return `Manage your job: ${this.job.title}`;
    },
    showBanner(): boolean {
      // always show banner for jobs with pending changes (new or edited)
      if (this.job.pendingChanges) return true;

      // if job is not active, don't show banner
      if (!this.job.isActive) return false;

      // finally, show the banner if job has new applicants
      return (this.job.receivedApplications?.totalCount ?? 0) > 0;
    },
    popupClasses(): Record<string, boolean> {
      return { '__with-banner': this.showBanner };
    },
    displayDots(): boolean {
      const parentOwnsJob = this.account.id === this.job.parent.id;
      const hasOpenApplicants = (this.job.applications?.totalCount ?? 0) > 0;

      // some jobs are owned by the parent, some are owned by family concierge. If the parent owns the job, we allow them to manage the job.
      // if the job has open applicants, we show the menu because it has a link to the applicants page
      return parentOwnsJob || hasOpenApplicants;
    },
    totalNewApplicants(): number {
      return this.job.receivedApplications?.totalCount ?? 0;
    },
    isPending(): boolean {
      return Boolean(this.job.pendingChanges);
    },
    showApplicantsBanner(): boolean {
      if (this.totalNewApplicants <= 0) return false;
      return this.isPending || this.isActive;
    },
    pendingReviewMessage(): string {
      return this.job.status === 'PENDING' ? 'Job post under review' : 'Job edits under review';
    },
    isActive(): boolean {
      return this.job.isActive;
    },
    isActiveOrPending(): boolean {
      return this.isActive || this.isPending;
    },
    newApplicantsTitle(): string {
      return `${this.totalNewApplicants} received ${pluralize('applicant', this.totalNewApplicants)}`;
    },
    totalApplicantsText(): string {
      const { totalCount } = this.job.applications;
      return `${totalCount} total ${pluralize('applicant', totalCount)}`;
    },
    jobDate(): string {
      return new JobPresenter(this.job).dateDescription();
    },
    fcOwnsJob(): boolean {
      if (!this.job.parent) return false;
      return !(this.account.id === this.job.parent.id);
    },
    jobLink(): RouteLocationRaw {
      if (this.totalNewApplicants > 0) {
        return { name: 'applications', params: { job_uuid: this.job.id } };
      }
      const isPending = this.job.status === 'PENDING';
      if (this.job.isActive || isPending) {
        return { name: 'seeker-jobs-open' };
      }
      return { name: 'seeker-jobs-closed' };
    },
    resolvedHref(): string {
      return this.$router.resolve(this.jobLink).href;
    },
  },
  methods: {
    emitJobClick(e: Event) {
      this.$emit('click', this, this.job, e);
    },
    emitManageJob() {
      this.$emit('manage-job', this.job);
    },
  },
});
</script>

<style lang="scss" scoped>
.seeker-job-card {
  flex-flow: column;
  background: white;
  box-shadow: 0 2px 4px 0 rgba(5, 7, 15, 0.2);
  border-radius: 4px;
  display: flex;
  transition: 0.5s box-shadow ease-in;

  &:hover {
    box-shadow: 0 4px 16px 0 rgba(5, 7, 15, 0.2);
  }
}

.job-text {
  @apply pt-0 pr-32 pb-4 pl-16;
}

.seeker-job-card-modal {
  margin-left: auto;
  position: absolute;
}
</style>
